import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { AuthLayout } from '@/components/AuthLayout'
import { Telemetry } from '@/lib/telemetry'

/**
 * The CTDCommons public signup form.
 *
 * IMPORTANT — email enumeration resistance (A2F-352):
 * The backend /auth/signup endpoint always returns the same 201 body
 * regardless of whether the email is new, a duplicate, or rejected by
 * Supabase as a 422. This page must therefore show the SAME success
 * screen for every 201. Do not branch the UI on the response body.
 * Distinguishing the paths would leak email-registration state, which
 * is exactly the attack the backend is hardened against.
 */

const API_BASE: string =
  (import.meta.env.VITE_API_BASE as string | undefined) ?? 'http://localhost:8000'

const TERMS_VERSION = 'ctdcommons-v1.0'

export function SignupPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError('')

    if (!acceptedTerms) {
      setError('You must agree to the Terms of Use to create an account.')
      return
    }

    setLoading(true)
    Telemetry.signupStarted()
    try {
      const trimmedEmail = email.trim()
      // Backend still requires organization_name and role_title with
      // min_length:1 (see backend/routers/auth.py:225-227). The Chrona Pro
      // add-in solved the same problem in chrona-addin/src/web/lib/useSignup.ts
      // by sending the email as a fallback; mirror that here so this commit
      // stays UI-only — no backend contract change.
      const res = await fetch(`${API_BASE}/api/v1/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: trimmedEmail,
          password,
          display_name: displayName.trim(),
          organization_name: trimmedEmail,
          role_title: trimmedEmail,
          terms_version: TERMS_VERSION,
        }),
      })

      if (res.status === 201) {
        // Same success state for new users AND silent duplicates. This
        // is the byte-identical response the backend guarantees — do
        // not inspect the body for a "duplicate" branch. By the same
        // logic, user_registered is fired on BOTH paths (we can't
        // distinguish client-side and must not try) — analytics will
        // overcount registrations slightly. That's acceptable noise.
        Telemetry.userRegistered()
        setSubmitted(true)
        return
      }

      // Service-availability errors are surfaced — these are NOT
      // enumeration channels (see services/public_signup_service.py).
      if (res.status === 503) {
        setError(
          'Signups are temporarily unavailable. Please try again later or ' +
            'contact support if the problem persists.',
        )
      } else if (res.status === 502) {
        setError(
          'We could not reach our identity service. Please try again in a moment.',
        )
      } else if (res.status === 429) {
        setError('Too many signup attempts. Please wait a few minutes and try again.')
      } else if (res.status === 422) {
        // Pydantic validation error on our side (e.g. password too short).
        setError('Please check your entries and try again.')
      } else {
        setError('Something went wrong. Please try again.')
      }
      Telemetry.errorSwallowed({
        error_type: 'signup_failure',
        operation: 'signup',
        screen_name: '/auth/signup',
        error_message: `status_${res.status}`,
      })
    } catch (err) {
      setError('Could not reach the server. Please check your connection.')
      Telemetry.errorSwallowed({
        error_type: 'network',
        operation: 'signup',
        screen_name: '/auth/signup',
        error_message: err instanceof Error ? err.message : 'unknown',
      })
    } finally {
      setLoading(false)
    }
  }

  if (submitted) {
    return (
      <AuthLayout
        title="Check your email"
        subtitle="We've sent a verification link to your inbox."
      >
        <div className="text-sm text-slate-600 leading-relaxed space-y-3">
          <p>
            Click the link in the email to confirm your address and activate
            your account. The link will expire in 24 hours.
          </p>
          <p className="text-xs text-slate-500">
            Didn't get an email? Check your spam folder, or wait a few minutes
            and try signing up again.
          </p>
        </div>
        <div className="mt-6 text-xs text-slate-500">
          <Link to="/" className="text-brand-orange hover:underline">
            &larr; Back to sign in
          </Link>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      title="Create an account"
      subtitle="Free access for regulatory affairs professionals."
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="email" className="block text-xs font-medium text-slate-600 mb-1">
            Work email
          </label>
          <input
            id="email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange"
          />
        </div>

        <div>
          <label
            htmlFor="password"
            className="block text-xs font-medium text-slate-600 mb-1"
          >
            Password
          </label>
          <input
            id="password"
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange"
          />
          <p className="mt-1 text-[11px] text-slate-400">At least 8 characters.</p>
        </div>

        <div>
          <label
            htmlFor="displayName"
            className="block text-xs font-medium text-slate-600 mb-1"
          >
            Full name
          </label>
          <input
            id="displayName"
            type="text"
            required
            autoComplete="name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange"
          />
        </div>

        <label className="flex items-start gap-2 text-xs text-slate-600 leading-relaxed cursor-pointer select-none">
          <input
            type="checkbox"
            required
            checked={acceptedTerms}
            onChange={(e) => setAcceptedTerms(e.target.checked)}
            className="mt-0.5 rounded border-slate-300 text-brand-orange focus:ring-brand-orange/30"
          />
          <span>
            I agree to the{' '}
            <a
              href="https://chronabio.ai/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-orange hover:underline"
            >
              Terms of Use
            </a>{' '}
            and understand that CTDCommons content is provided for research
            purposes only.
          </span>
        </label>

        {error && (
          <div className="px-3 py-2 rounded-md bg-red-50 border border-red-200 text-xs text-red-700">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading || !acceptedTerms}
          className="w-full px-4 py-2 bg-brand-orange text-white text-sm font-medium rounded-md hover:bg-brand-orange/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Creating account...' : 'Create account'}
        </button>
      </form>

      <div className="mt-6 text-center text-xs text-slate-500">
        Already have an account?{' '}
        <Link to="/" className="text-brand-orange hover:underline">
          Sign in
        </Link>
      </div>
    </AuthLayout>
  )
}
